import * as React from 'react'
import { Card, FormGroup } from '@blueprintjs/core'
import { useSelector } from 'react-redux'
import Client from '../../client'
import { LoadableSelect } from '../../components/Inputs'
import { useSupplierSession } from './hooks/useSupplierSession'
import { useAmazonBookSearchWithNavigation } from './hooks/useAmazonBookSearchWithNavigation'
import AmazonBookSearchInput from '../scan-books/AmazonBookSearchInput'
import AmazonBookResults from '../scan-books/AmazonBookResults'
import './style.scss'
import { AppState } from '../../store'
import CHANNELS from '../../constants/Channels'
import { LinkAmazonAccountWarning } from '../scan-books/LinkAmazonAccount'

export const ScanToListBooks: React.FC = () => {
  const [supplierId, setSupplierId] = useSupplierSession()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const {
    query,
    inputquery,
    books,
    loading,
    loadingSort,
    selectedBookIndex,
    sortDecision,
    handleSelectBook,
    handleSearch,
    handleQueryChange
  } = useAmazonBookSearchWithNavigation()

  React.useEffect(() => {
    if (supplierId && inputRef.current) {
      const timeoutId = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }, 100)

      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [supplierId])

  const connectedMarketAccounts = useSelector((s: AppState) => s.current.organization.connectedMarketAccounts)
  const connectedMarketAccount = connectedMarketAccounts.find(
    marketAccount => marketAccount.marketId === CHANNELS.AMAZON.id
  )

  if (!connectedMarketAccount) {
    return (
      <div className="container">
        <LinkAmazonAccountWarning />
      </div>
    )
  }

  const getCardClassName = () => {
    let className = 'scan-form-card'
    if (sortDecision === 'ecommerce') {
      className += ' scan-form-card--ecommerce'
    } else if (sortDecision === 'store') {
      className += ' scan-form-card--store'
    }
    return className
  }

  return (
    <div className="scan-to-list-books-page">
      <div className="page-header">
        <h1>Scan to list books</h1>
      </div>

      <div className="form-container">
        <Card className={getCardClassName()}>
          <FormGroup label="Category">
            <div className="category-input">ISBN: Books, Movies, Music</div>
          </FormGroup>

          <FormGroup label="Supplier">
            <LoadableSelect
              className="supplier-select"
              load={Client.suppliersIndex}
              process={d => d.suppliers.map(s => ({ value: s.id, label: s.name }))}
              onChange={setSupplierId}
              value={supplierId}
              placeholder="Select supplier"
              autoload
            />
          </FormGroup>

          {supplierId && (
            <div className="amazon-book-search">
              <div className="amazon-book-search-input-section">
                <AmazonBookSearchInput
                  ref={inputRef}
                  value={query}
                  onChange={handleQueryChange}
                  onSearch={handleSearch}
                  loading={loading}
                  loadingSort={loadingSort}
                  inputquery={inputquery}
                  sortDecision={sortDecision}
                  description="Scan/enter ISBN or title to start listing"
                />
              </div>
            </div>
          )}
        </Card>

        {!loading && books.length > 0 && (
          <div className="amazon-book-search-results-section">
            <AmazonBookResults
              books={books}
              setSelectedBookIndex={handleSelectBook}
              selectedBookIndex={selectedBookIndex}
            />
          </div>
        )}

        {!loading && inputquery && books.length === 0 && (
          <div className="amazon-book-search-results-section">
            <h3>Search results</h3>
            <div className="amazon-book-empty-state">
              <div className="amazon-book-empty-card">No results found</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ScanToListBooks
